import { useEffect, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  ChalkboardTeacher, Books, Exam, CalendarBlank, CheckCircle, Sparkle, ArrowUpRight,
} from "@phosphor-icons/react";

const CLASSES_TODAY = [
  { t: "09:00", code: "CS301", title: "Data Structures · Trees & Graphs", room: "LH-201", students: 184, taken: true },
  { t: "11:00", code: "MA201", title: "Linear Algebra · Eigen-decomposition", room: "LH-104", students: 240, taken: false, next: true },
  { t: "14:00", code: "CS301-Lab", title: "Lab: AVL Trees", room: "Lab-3", students: 64, taken: false },
];

const GRADE_DIST = [
  { score: "Q1", count: 24 }, { score: "Q2", count: 48 },
  { score: "Q3", count: 62 }, { score: "Q4", count: 38 }, { score: "Q5", count: 12 },
];

const PENDING = [
  { type: "Assignment", title: "Trees & Graphs — Problem set 4", course: "CS301", count: 142, due: "Feb 18" },
  { type: "Project Review", title: "Capstone Mid Review", course: "PR101", count: 8, due: "Feb 21" },
  { type: "Exam Eval", title: "Mid-Sem Online — On-screen evaluation", course: "CS301", count: 184, due: "Feb 28" },
];

export default function FacultyDashboard() {
  const { user } = useAuth();
  const [classCount, setClassCount] = useState(3);

  useEffect(() => { setClassCount(CLASSES_TODAY.length); }, []);

  return (
    <div className="p-6 lg:p-8 space-y-8" data-testid="faculty-dashboard">
      <div className="grid lg:grid-cols-12 gap-5 items-end">
        <div className="lg:col-span-8">
          <div className="eos-overline">Faculty workspace</div>
          <h1 className="font-heading font-extrabold text-4xl tracking-tight mt-1">
            Good morning, {user?.name?.split(" ")[0] || "Professor"}.
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {classCount} classes today · 488 students under your supervision · 3 evaluations pending.
          </p>
        </div>
        <div className="lg:col-span-4 flex lg:justify-end gap-2">
          <Button variant="outline" className="rounded-md"><CalendarBlank size={14} className="mr-1.5"/> My week</Button>
          <Button className="rounded-md bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]"><Sparkle size={14} weight="fill" className="mr-1.5"/> Mark attendance</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border rounded-md overflow-hidden">
        {[
          { l: "Classes today", v: classCount, t: "next at 11:00 AM" },
          { l: "Students", v: 488, t: "across 4 courses" },
          { l: "Attendance avg.", v: "92.1%", t: "this term" },
          { l: "Pending evals", v: 14, t: "blind / double" },
        ].map(s => (
          <div key={s.l} className="bg-white p-5">
            <div className="eos-overline">{s.l}</div>
            <div className="font-heading font-extrabold text-3xl mt-2 tracking-tight">{s.v}</div>
            <div className="text-[11px] text-muted-foreground mt-1">{s.t}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-12 gap-5">
        <div className="lg:col-span-7 eos-card p-0 overflow-hidden">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <ChalkboardTeacher size={16} weight="duotone"/>
            <div className="font-heading font-semibold text-base">Today's classes</div>
          </div>
          <div className="divide-y divide-border">
            {CLASSES_TODAY.map((c) => (
              <div key={`${c.t}-${c.code}`} className={`p-4 flex items-center gap-4 ${c.next ? "bg-[hsl(20_90%_48%)]/5" : ""}`}>
                <div className="font-mono-eos text-xs text-muted-foreground w-12">{c.t}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{c.code} · {c.title}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{c.room} · {c.students} students</div>
                </div>
                {c.taken ? (
                  <span className="text-[11px] inline-flex items-center gap-1 text-emerald-700 px-2 py-1 rounded-md bg-emerald-50">
                    <CheckCircle size={11} weight="bold"/> Attendance marked
                  </span>
                ) : c.next ? (
                  <Button className="h-8 px-3 text-xs rounded bg-[hsl(20_90%_48%)] hover:bg-[hsl(20_90%_42%)]">Start class</Button>
                ) : (
                  <Button variant="outline" className="h-8 px-3 text-xs rounded">Open</Button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 eos-card">
          <div className="eos-overline">Last assessment · CS301 Quiz 3</div>
          <div className="font-heading font-bold text-xl mt-1">Score distribution · 184 papers</div>
          <div className="h-44 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={GRADE_DIST}>
                <XAxis dataKey="score" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))"/>
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))"/>
                <Tooltip contentStyle={{ borderRadius: 6, fontSize: 12, border: "1px solid hsl(var(--border))" }}/>
                <Line type="monotone" dataKey="count" stroke="hsl(244 53% 20%)" strokeWidth={2.5} dot={{ r: 4, fill: "hsl(20 90% 48%)" }}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[11px] text-muted-foreground">Median 72 · 5 students &lt; 35 — Copilot recommends remedial session.</div>
        </div>
      </div>

      <div className="eos-card p-0 overflow-hidden">
        <div className="p-4 border-b border-border flex items-center gap-2">
          <Exam size={16} weight="duotone"/>
          <div className="font-heading font-semibold text-base">Pending evaluations</div>
        </div>
        <div className="divide-y divide-border">
          {PENDING.map((p) => (
            <div key={`${p.course}-${p.title}`} className="p-4 flex items-center gap-4">
              <div className="w-9 h-9 rounded-md bg-secondary flex items-center justify-center">
                <Books size={16} weight="duotone" className="text-[hsl(244_53%_20%)]"/>
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium">{p.title}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{p.type} · {p.course} · Due {p.due}</div>
              </div>
              <div className="font-mono-eos text-sm">{p.count}</div>
              <Button variant="outline" className="h-8 px-3 text-xs rounded">Evaluate <ArrowUpRight size={11} className="ml-1" weight="bold"/></Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
